/**
 * ServicesTiers — fixed-price service packages displayed as cards.
 *
 * Each package has a configurable action "flavor":
 *   "book"  → scroll to the booking section
 *   "quote" → scroll to the quote form section
 *   "buy"   → scroll to / open checkout (deferred)
 *
 * Registered as: services / services-tiers
 */
import React from "react";
import type { SectionComponentProps } from "../../registry.js";

interface ServicePackage {
  id?: string;
  name: string;
  tagline?: string;
  priceLabel: string;         // e.g. "from $89", "$249", "Starting at $55/visit"
  features?: string[];
  flavor?: "book" | "quote" | "buy"; // per-package action
  ctaLabel?: string;          // override default label
  highlight?: boolean;        // visually emphasised / "most popular"
}

interface ServicesTiersProps {
  headline?: string;
  subheadline?: string;
  packages?: ServicePackage[];
  /** Default flavor when a package doesn't specify its own */
  defaultFlavor?: "book" | "quote" | "buy";
  bookTarget?: string;   // default "#book"
  quoteTarget?: string;  // default "#quote"
}

const DEFAULT_PACKAGES: ServicePackage[] = [
  { name: "Basic Wash", priceLabel: "$89", features: ["Exterior hand wash", "Wheel cleaning", "Windows"], flavor: "book" },
  { name: "Full Detail", priceLabel: "$189", features: ["Full exterior + interior", "Vacuum & wipe-down", "Tire shine"], flavor: "book", highlight: true },
  { name: "Ceramic Coat", priceLabel: "from $399", features: ["Paint correction", "Ceramic coating", "12-month protection"], flavor: "book" },
];

function CheckIcon() {
  return (
    <svg className="w-4 h-4 text-primary flex-shrink-0" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M3 8L6.5 11.5L13 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function ServicesTiers({ props }: SectionComponentProps) {
  const {
    headline = "Our Services",
    subheadline,
    packages = DEFAULT_PACKAGES,
    defaultFlavor = "book",
    bookTarget = "#book",
    quoteTarget = "#quote",
  } = props as ServicesTiersProps;

  function getTarget(pkg: ServicePackage) {
    const flavor = pkg.flavor ?? defaultFlavor;
    if (flavor === "book") return bookTarget;
    if (flavor === "quote") return quoteTarget;
    return "#";
  }

  function getLabel(pkg: ServicePackage) {
    if (pkg.ctaLabel) return pkg.ctaLabel;
    const flavor = pkg.flavor ?? defaultFlavor;
    if (flavor === "book") return "Book Now";
    if (flavor === "quote") return "Get a Quote";
    return "Learn More";
  }

  return (
    <section className="py-20 px-6 bg-background" id="services">
      <div className="max-w-5xl mx-auto">
        {(headline || subheadline) && (
          <div className="text-center mb-12">
            {headline && (
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">{headline}</h2>
            )}
            {subheadline && (
              <p className="text-muted-foreground text-base max-w-xl mx-auto leading-relaxed">{subheadline}</p>
            )}
          </div>
        )}

        <div className={[
          "grid gap-6",
          packages.length === 2 ? "md:grid-cols-2 max-w-2xl mx-auto" :
          packages.length === 3 ? "md:grid-cols-3" :
          "md:grid-cols-2 lg:grid-cols-4",
        ].join(" ")}>
          {packages.map((pkg, i) => (
            <div
              key={pkg.id ?? pkg.name ?? i}
              data-stagger-child
              className={[
                "relative flex flex-col rounded-2xl border p-6 transition-shadow",
                pkg.highlight
                  ? "border-primary/40 bg-primary/5 shadow-[0_8px_40px_-8px_rgba(0,0,0,0.15)]"
                  : "border-border bg-background hover:shadow-md",
              ].join(" ")}
            >
              {pkg.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full">
                  Most Popular
                </span>
              )}

              <div className="mb-4">
                <h3 className="font-bold text-lg text-foreground">{pkg.name}</h3>
                {pkg.tagline && (
                  <p className="text-sm text-muted-foreground mt-0.5">{pkg.tagline}</p>
                )}
              </div>

              <div className="mb-5">
                <span className="text-2xl font-extrabold text-foreground tabular-nums">{pkg.priceLabel}</span>
              </div>

              {pkg.features && pkg.features.length > 0 && (
                <ul className="space-y-2.5 mb-6 flex-1">
                  {pkg.features.map((feat, fi) => (
                    <li key={fi} className="flex items-start gap-2.5 text-sm text-foreground">
                      <CheckIcon />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
              )}

              <a
                href={getTarget(pkg)}
                className={[
                  "mt-auto block w-full rounded-xl px-5 py-2.5 text-sm font-semibold text-center transition-all",
                  pkg.highlight
                    ? "bg-primary text-primary-foreground hover:opacity-90"
                    : "border border-primary/50 text-primary hover:bg-primary/5",
                ].join(" ")}
              >
                {getLabel(pkg)}
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
